import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { ApiError } from "../../../lib/http";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import {
  fetchPrepaidTerms,
  isPrepaidUnavailable,
} from "../services/prepaidService";
import type { PrepaidTerm, PrepaidTerms } from "../types/prepaid.types";

export type PrepaidTermsStatus = "loading" | "ready" | "failed";

export interface PrepaidTermsState {
  /** The term covering today, or `null` for a practice with none. */
  current: PrepaidTerm | null;
  /** The term bought to follow it, or `null`. **It grants nothing until its
   *  own start date** — it is what has been paid for, not what is running. */
  queued: PrepaidTerm | null;
  status: PrepaidTermsStatus;
  /**
   * Whether the read failed because prepaid billing is switched off on this
   * backend — the `404 prepaid_billing_unavailable` every path here answers
   * while the flag is down, rather than a call that went wrong.
   *
   * Worth telling apart from an ordinary failure by anything that reasons about
   * what a practice owns: with the feature off nobody can hold a term, so no
   * terms is a fact rather than a gap. See `accessLapse`.
   */
  unavailable: boolean;
  error?: string;
  reload: () => void;
}

const NO_TERMS: PrepaidTerms = { current: null, queued: null };

export const PREPAID_TERMS_QUERY_KEY = ["billing", "prepaid", "terms"] as const;

/**
 * "Your plan" and "your next plan", from `GET /billing/prepaid/terms/`.
 *
 * The only call that can answer the second. `GET /billing/subscription/` shows
 * the active row and nothing else, so a renewal already paid for is invisible
 * there — and on the first morning of one, for the ten minutes until the
 * backend's sweep promotes it, that endpoint can still be showing the term that
 * ended yesterday.
 *
 * **It is not the access decision.** Whether the product is open is
 * `access_source` on `useTrialAccess`, which already counts a scheduled term
 * whose day has come. This hook says what was bought and until when; that one
 * says whether the door is open. A screen that locked a feature on these dates
 * would shut a practice out for the ten minutes above.
 *
 * Two nulls is an ordinary answer and `ready`, not a failure: it is every
 * practice that has never bought a term, and also one still on an old mandate,
 * which the backend answers quietly here and names when a quote is asked for.
 *
 * Backed by TanStack Query like the rest of this feature, so a tab opened again
 * shows the last answer while a fresh read checks it behind the scenes. A term
 * bought on another device catches up within moments, and nothing here is a
 * figure anybody is charged from.
 *
 * Owner only. A staff member gets `403 billing_owner_required`, which is a
 * permission rather than a failure to retry — the price list is the one prepaid
 * call they may make.
 *
 * Must be used inside a Clerk provider.
 */
export function usePrepaidTerms(): PrepaidTermsState {
  const getToken = useTokenGetter();

  const query = useQuery({
    queryKey: PREPAID_TERMS_QUERY_KEY,
    queryFn: ({ signal }) => fetchPrepaidTerms(getToken, signal),
  });

  const terms = query.data ?? NO_TERMS;

  useEffect(() => {
    if (query.isError)
      console.error("prepaid terms failed to load:", query.error);
  }, [query.isError, query.error]);

  return {
    current: terms.current,
    queued: terms.queued,
    unavailable: isPrepaidUnavailable(query.error),
    status:
      query.status === "pending"
        ? "loading"
        : query.status === "error"
          ? "failed"
          : "ready",
    error:
      query.isError && query.error instanceof ApiError
        ? query.error.message
        : query.isError
          ? "We could not reach the server. Check your connection and try again."
          : undefined,
    reload: () => {
      void query.refetch();
    },
  };
}

export default usePrepaidTerms;
